import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabase/client'
import './BirthLocationPage.css' // Предполагается, что будет создан CSS файл

const BirthLocationPage = ({ updateUserData, fetchTalentIndices }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [cityResults, setCityResults] = useState([])
  const [selectedCity, setSelectedCity] = useState(null)
  const [searchError, setSearchError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const navigate = useNavigate()

  // Поиск городов по запросу
  const searchCities = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Пожалуйста, введите название города')
      return
    }

    try {
      setIsSearching(true)
      setSearchError('')
      console.log(`Поиск города: ${searchQuery}`)

      const { data, error } = await supabase.functions.invoke('search-city', {
        body: { q: searchQuery },
      })
      if (error) {
        console.error('Ошибка при поиске города:', error)
        setSearchError('Ошибка при поиске города. Пожалуйста, попробуйте еще раз.')
        return
      }

      console.log(`Найдено городов: ${data.length}`)

      if (data.length === 0) {
        setSearchError('Города не найдены. Попробуйте другой запрос.')
      } else {
        setCityResults(data)
      }
    } catch (error) {
      console.error('Error searching cities:', error)
      setSearchError('Ошибка при поиске города. Пожалуйста, попробуйте еще раз.')
    } finally {
      setIsSearching(false)
    }
  }

  // Обработка выбора города
  const handleCitySelect = (city) => {
    console.log(`Выбран город: ${city.city}, ${city.land}`)
    setSelectedCity(city)
    setCityResults([])
  }

  // Получение данных о рождении
  const getBirthDetails = () => {
    try {
      const birthDetailsStr = localStorage.getItem('birthDetails')
      if (!birthDetailsStr) {
        console.log('Данные о рождении не найдены в localStorage')
        return null
      }

      const birthDetails = JSON.parse(birthDetailsStr)
      console.log('Данные о рождении получены из localStorage')
      return birthDetails
    } catch (error) {
      console.error('Ошибка при получении данных о рождении:', error)
      return null
    }
  }

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedCity) {
      setSearchError('Пожалуйста, выберите город из списка')
      return
    }

    setIsLoading(true)
    console.log('Начинаем процесс расчета талантов...')

    try {
      // Получаем данные о рождении
      const birthDetails = getBirthDetails()

      if (!birthDetails || !birthDetails.birthDate) {
        throw new Error('Не указана дата рождения')
      }

      // Извлекаем дату рождения
      const birthDate = new Date(birthDetails.birthDate)
      const year = birthDate.getFullYear()
      const month = birthDate.getMonth() + 1
      const day = birthDate.getDate()

      // Получаем время рождения
      let hour = 12,
        minute = 0

      // Используем timeForAPI из обновленного формата данных, если оно есть
      if (birthDetails.timeForAPI) {
        const [h, m] = birthDetails.timeForAPI.split(':')
        hour = parseInt(h, 10)
        minute = parseInt(m, 10)
      } else if (birthDetails.exactTime) {
        // Обратная совместимость со старым форматом
        const [h, m] = birthDetails.exactTime.split(':')
        hour = parseInt(h, 10)
        minute = parseInt(m, 10)
      }

      // Формируем данные для расчета талантов
      const birthData = {
        gender: 'M', // По умолчанию, в будущем можно добавить выбор
        year,
        month,
        day,
        hour,
        minute,
        tz: selectedCity.tz,
        city: selectedCity.city,
      }

      console.log('Данные для расчета талантов:', birthData)

      // Сохраняем данные о местоположении
      updateUserData('birthLocation', {
        city: selectedCity.city,
        country: selectedCity.land,
        timezone: selectedCity.tz,
      })

      // Получаем таланты через API
      console.log('Вызываем API для расчета талантов...')
      const talentIndices = await fetchTalentIndices(birthData)

      console.log(`Получено ${talentIndices ? talentIndices.length : 0} индексов талантов`)

      // Переходим к опроснику
      console.log('Переходим к странице опросника...')
      navigate('/questionnaire')
    } catch (error) {
      console.error('Ошибка при обработке данных:', error)
      setSearchError(`Произошла ошибка: ${error.message}`)
      setIsLoading(false)
    }
  }

  return (
    <div className="container">
      <div className="header">
        <div className="logo">
          Business <span>Evolution AI</span>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-bar-inner" style={{ width: '75%' }}></div>
      </div>

      <h2>Место рождения</h2>

      <div className="info-box">
        <p>
          Продолжительность светового дня и интенсивность солнечного излучения в месте рождения
          могут влиять на настройку ваших биологических часов и хронотип.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="citySearch">
            Укажите город рождения (если вашего города нет в списке, укажите ближайший крупный
            город):
          </label>
          <div className="search-container">
            <input
              type="text"
              id="citySearch"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Введите город рождения"
              className={searchError ? 'error-input' : ''}
            />
            <button
              type="button"
              className="btn-search"
              onClick={searchCities}
              disabled={isSearching}
            >
              {isSearching ? 'Поиск...' : 'Найти'}
            </button>
          </div>
          {searchError && <div className="error">{searchError}</div>}
        </div>

        {cityResults.length > 0 && (
          <div className="city-results">
            <h3>Результаты поиска:</h3>
            <ul className="city-list">
              {cityResults.map((city, index) => (
                <li
                  key={index}
                  className={
                    selectedCity &&
                    selectedCity.city === city.city &&
                    selectedCity.land === city.land
                      ? 'selected'
                      : ''
                  }
                  onClick={() => handleCitySelect(city)}
                >
                  {city.city}, {city.land} <span>(Часовой пояс: {city.tz})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {selectedCity && (
          <div className="selected-city">
            <h3>Выбранный город:</h3>
            <div className="city-card">
              <div className="city-name">
                {selectedCity.city}, {selectedCity.land}
              </div>
              <div className="city-timezone">Часовой пояс: {selectedCity.tz}</div>
            </div>
          </div>
        )}

        <button type="submit" className="btn" disabled={isLoading || !selectedCity}>
          {isLoading ? 'Обработка...' : 'Продолжить'}
        </button>
      </form>
    </div>
  )
}

export default BirthLocationPage
